import { useMemo, useState, useEffect, useRef } from "react";
import AnnouncementPrimaryIcon from "@/assets/icons/announcement-primary.svg";
import { useApp } from "@/app/app-context";
import { garageRegisterIdQueryFromEntity } from "@/lib/garage-register-entity-scope";
import { useGarageRegisterAnnouncements } from "@/hooks/queries/garage-register/use-announcements";
import { useGetReadyAnnouncements } from "@/hooks/queries/get-ready/use-announcements";

/**
 * Active marquee announcement shape — what the banner consumes.
 * Both Garage Register and Get Ready domain types satisfy this, so
 * we can merge from either source without any adapter.
 */
interface MarqueeAnnouncement {
  id: string;
  title: string;
  body: string;
  color?: string;
  lifetimeRange: { from: Date; to: Date };
  is_archived?: boolean;
}

export function BroadcastBanner() {
  const { selectedEntity, currentApp } = useApp();
  const appKey = currentApp?.key;

  // ── Garage Register source ───────────────────────────────────────
  const garageRegisterId = garageRegisterIdQueryFromEntity(selectedEntity);
  const grEnabled = appKey === "garageregister" && Boolean(selectedEntity);
  const { data: grAnnouncements } = useGarageRegisterAnnouncements(
    grEnabled
      ? {
          ...(garageRegisterId != null
            ? { garage_register_id: garageRegisterId }
            : {}),
        }
      : undefined,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { enabled: grEnabled } as any,
    grEnabled
      ? {
          params: {
            ...(garageRegisterId != null
              ? { garage_register_id: garageRegisterId }
              : {}),
          },
        }
      : undefined
  );

  // ── Get Ready source ─────────────────────────────────────────────
  const grdyEnabled = appKey === "getready" && Boolean(selectedEntity);
  const grdyParams = grdyEnabled
    ? { dealership_id: selectedEntity?.entity_id }
    : undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: getReadyAnnouncementsPage } = useGetReadyAnnouncements(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (grdyParams ?? undefined) as any,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { enabled: grdyEnabled } as any,
    grdyEnabled ? { params: grdyParams } : undefined
  );

  // Get Ready service wraps responses in a paginated envelope; GR
  // returns a flat array. Normalize both to a single array of
  // marquee-shaped rows.
  const sourceAnnouncements: MarqueeAnnouncement[] = useMemo(() => {
    if (appKey === "garageregister") {
      return Array.isArray(grAnnouncements)
        ? (grAnnouncements as MarqueeAnnouncement[])
        : [];
    }
    if (appKey === "getready") {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const page = getReadyAnnouncementsPage as any;
      const rows = page?.results ?? (Array.isArray(page) ? page : []);
      return (rows as { id: string; title: string; body: string; color?: string; lifetimeRange: { from: Date; to: Date }; isArchived?: boolean }[]).map(
        (r) => ({
          id: r.id,
          title: r.title,
          body: r.body,
          color: r.color,
          lifetimeRange: r.lifetimeRange,
          is_archived: r.isArchived,
        })
      );
    }
    return [];
  }, [appKey, grAnnouncements, getReadyAnnouncementsPage]);

  // Filter active announcements (not archived and within date range)
  const activeAnnouncements = useMemo(() => {
    if (!sourceAnnouncements.length) return [];

    const now = new Date();
    return sourceAnnouncements.filter((announcement) => {
      if (announcement.is_archived) return false;

      const startDate = announcement.lifetimeRange.from;
      const endDate = announcement.lifetimeRange.to;

      return now >= startDate && now <= endDate;
    });
  }, [sourceAnnouncements]);

  // Format announcements for display
  const announcementContent = useMemo(() => {
    if (!activeAnnouncements || activeAnnouncements.length === 0) {
      return {
        text: "Welcome to the new Dealers Hub platform - Streamlined workflows and enhanced features for better efficiency",
        elements: null,
      };
    }

    const elements = activeAnnouncements.flatMap((announcement, index) => {
      const spacing = index > 0 ? <span key={`spacing-${index}`} className="inline-block" style={{ width: '60rem' }} /> : null;
      return [
        spacing,
        <span
          key={announcement.id}
          data-announcement-index={index}
          className="announcement-item"
          style={{ color: announcement.color || 'inherit' }}
        >
          <strong>{announcement.title}</strong>: {announcement.body}
        </span>,
      ].filter(Boolean);
    });

    const text = activeAnnouncements
      .map((announcement) => `${announcement.title}: ${announcement.body}`)
      .join(" ".repeat(50)); // Add spacing equivalent to ~4rem

    return { text, elements };
  }, [activeAnnouncements]);

  // Track which announcement is currently visible
  const [currentAnnouncementIndex, setCurrentAnnouncementIndex] = useState(0);
  const marqueeContainerRef = useRef<HTMLDivElement>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);

  // Calculate animation duration based on content length (slower)
  const animationDuration = useMemo(() => {
    const baseDuration = 20; // base 20 seconds (slower)
    const contentLength = announcementContent.text.length;
    // Add 2 seconds per 50 characters
    return Math.max(baseDuration, baseDuration + Math.floor(contentLength / 50) * 2);
  }, [announcementContent.text]);

  // Reset index when announcements change
  useEffect(() => {
    setCurrentAnnouncementIndex(0);
  }, [activeAnnouncements]);

  // Track visible announcement based on element positions
  useEffect(() => {
    if (!activeAnnouncements || activeAnnouncements.length === 0) return;
    if (!marqueeContainerRef.current) return;

    const container = marqueeContainerRef.current;
    const marqueeText = container.querySelector('.marquee-text') as HTMLElement;
    if (!marqueeText) return;

    const checkVisibility = () => {
      const containerRect = container.getBoundingClientRect();
      const containerLeft = containerRect.left;
      const containerRight = containerRect.right;
      const containerCenter = containerLeft + containerRect.width / 2;

      const announcementElements = container.querySelectorAll('.announcement-item');
      if (announcementElements.length === 0) return;

      let closestIndex = 0;
      let closestDistance = Infinity;

      announcementElements.forEach((element) => {
        const elementRect = element.getBoundingClientRect();
        const elementCenter = elementRect.left + elementRect.width / 2;

        // Calculate distance from container center
        const distance = Math.abs(elementCenter - containerCenter);

        // Check if element is at least partially visible
        const isVisible = elementRect.right > containerLeft && elementRect.left < containerRight;

        if (isVisible && distance < closestDistance) {
          closestDistance = distance;
          const index = parseInt(
            (element as HTMLElement).getAttribute('data-announcement-index') || '0',
            10
          );
          closestIndex = index;
        }
      });

      setCurrentAnnouncementIndex(closestIndex);
      animationFrameRef.current = requestAnimationFrame(checkVisibility);
    };

    // Start checking visibility
    animationFrameRef.current = requestAnimationFrame(checkVisibility);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [activeAnnouncements, announcementContent.elements]);

  // Get the current visible announcement's color for banner styling
  const bannerColor = useMemo(() => {
    if (!activeAnnouncements || activeAnnouncements.length === 0) return 'inherit';
    const currentAnnouncement = activeAnnouncements[currentAnnouncementIndex];
    return currentAnnouncement?.color || 'inherit';
  }, [activeAnnouncements, currentAnnouncementIndex]);

  return (
    <>
      <style>
        {`
          @keyframes marquee {
            0% {
              transform: translateX(70vw);
            }
            100% {
              transform: translateX(-100%);
            }
          }
          
          .marquee-text {
            animation: marquee ${animationDuration}s linear infinite;
            left: 0;
          }
          
          /* Pause animation on hover */
          .marquee-container:hover .marquee-text {
            animation-play-state: paused;
          }
          
          ${bannerColor !== 'inherit' ? `
            .banner-icon-${bannerColor.replace(/[^a-zA-Z0-9]/g, '-')} {
              color: ${bannerColor} !important;
            }
            .banner-icon-${bannerColor.replace(/[^a-zA-Z0-9]/g, '-')} svg,
            .banner-icon-${bannerColor.replace(/[^a-zA-Z0-9]/g, '-')} path,
            .banner-icon-${bannerColor.replace(/[^a-zA-Z0-9]/g, '-')} * {
              fill: currentColor !important;
              color: ${bannerColor} !important;
            }
          ` : ''}
        `}
      </style>

      <div
        className="h-7 flex flex-1 items-center gap-1 overflow-hidden rounded-sm relative"
      >
        {/* Background layer with announcement color */}
        <div
          className="absolute inset-0 rounded-sm"
          style={bannerColor !== 'inherit' ? {
            backgroundColor: bannerColor,
            opacity: 0.1
          } : {
            backgroundColor: 'hsl(var(--primary) / 0.1)'
          }}
        />
        <div className="pl-4 flex items-center gap-1 flex-shrink-0 relative z-10">
          <span
            className="font-normal text-sm"
            style={{ color: bannerColor !== 'inherit' ? bannerColor : undefined }}
          >
            Dealers Hub
          </span>
          <div
            className={bannerColor !== 'inherit' ? `banner-icon-${bannerColor.replace(/[^a-zA-Z0-9]/g, '-')}` : ''}
            style={bannerColor !== 'inherit' ? { color: bannerColor } : undefined}
          >
            <AnnouncementPrimaryIcon />
          </div>
        </div>
        <div
          ref={marqueeContainerRef}
          className="flex-1 h-full relative overflow-hidden marquee-container z-10"
        >
          <span className="marquee-text whitespace-nowrap absolute top-1 text-sm text-primary">
            {announcementContent.elements || announcementContent.text}
          </span>
        </div>
      </div>
    </>
  );
}
